import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Edit2, Trash2, Loader2, Save, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const VehicleTypeMaster = () => {
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [editId, setEditId] = useState(null);
  const [typeName, setTypeName] = useState('');
  const [sizesText, setSizesText] = useState('');

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json'
  }), []);

  const fetchVehicleTypes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${backendUrl}/api/vehicle-types`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch');
      setVehicleTypes(await res.json());
    } catch {
      toast({ title: "Error", description: "Failed to load vehicle types", variant: "destructive" });
    } finally { setLoading(false); }
  }, [backendUrl, getAuthHeaders, toast]);

  useEffect(() => { fetchVehicleTypes(); }, [fetchVehicleTypes]);

  const resetForm = () => {
    setEditId(null);
    setTypeName('');
    setSizesText('');
  };

  const handleEdit = (record) => {
    setEditId(record.id);
    setTypeName(record.type_name);
    setSizesText(record.sizes.map(s => s.size_name).join(', '));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSave = async () => {
    if (!typeName.trim()) {
      toast({ title: "Validation Error", description: "Vehicle Type is required", variant: "destructive" }); return;
    }
    const validSizes = sizesText.split(',').map(s => s.trim()).filter(Boolean);
    if (validSizes.length === 0) {
      toast({ title: "Validation Error", description: "At least one Vehicle Size is required", variant: "destructive" }); return;
    }
    const uniqueSizes = [...new Set(validSizes)];
    if (uniqueSizes.length !== validSizes.length) {
      toast({ title: "Validation Error", description: "Duplicate vehicle sizes are not allowed", variant: "destructive" }); return;
    }

    try {
      setSaving(true);
      const payload = { type_name: typeName.trim(), sizes: validSizes.map(s => ({ size_name: s })) };
      const res = await fetch(
        editId ? `${backendUrl}/api/vehicle-types/${editId}` : `${backendUrl}/api/vehicle-types`,
        { method: editId ? 'PUT' : 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload) }
      );
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed to save'); }
      toast({ title: "Success", description: editId ? "Vehicle type updated" : "Vehicle type created" });
      resetForm();
      fetchVehicleTypes();
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!recordToDelete) return;
    try {
      const res = await fetch(`${backendUrl}/api/vehicle-types/${recordToDelete.id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to delete');
      toast({ title: "Success", description: "Vehicle type deleted" });
      fetchVehicleTypes();
    } catch {
      toast({ title: "Error", description: "Failed to delete vehicle type", variant: "destructive" });
    } finally { setDeleteDialogOpen(false); setRecordToDelete(null); }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-text-dark font-manrope">Vehicle Type Master</h1>
        <p className="text-text-secondary font-ibm mt-1">Manage vehicle types and their sizes</p>
      </div>

      {/* ADD FORM */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b-4 border-accent-blue px-6 py-4">
          <h2 className="text-base font-semibold text-text-dark font-manrope tracking-wide">
            {editId ? 'EDIT VEHICLE TYPE' : 'ADD VEHICLE TYPE'}
          </h2>
        </div>
        <div className="p-6 space-y-5">
          {/* Vehicle Type */}
          <div className="space-y-1.5">
            <Label className="text-text-dark font-ibm font-medium text-sm">
              Vehicle Type <span className="text-accent-red">*</span>
            </Label>
            <Input
              data-testid="vehicle-type-name-input"
              value={typeName}
              onChange={e => setTypeName(e.target.value)}
              placeholder="e.g. REEFER VEHICLE"
              className="w-64 border-gray-300 focus:border-accent-blue font-ibm"
            />
          </div>

          {/* Vehicle Sizes — comma-separated */}
          <div className="space-y-1.5">
            <Label className="text-text-dark font-ibm font-medium text-sm">
              Vehicle Size <span className="text-accent-red">*</span>
            </Label>
            <Input
              data-testid="vehicle-sizes-input"
              value={sizesText}
              onChange={e => setSizesText(e.target.value)}
              placeholder="e.g. 14 FT, 8 FT, 24 FT, 16 FT"
              className="w-96 border-gray-300 focus:border-accent-blue font-ibm"
            />
            <p className="text-xs text-text-secondary font-ibm">Separate multiple sizes with a comma</p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-2">
            <Button
              data-testid="save-vehicle-type-btn"
              onClick={handleSave}
              disabled={saving}
              className="bg-accent-green hover:bg-accent-green/90 text-white font-ibm font-semibold h-10 px-6 rounded-lg"
              style={{ backgroundColor: '#22c55e' }}
            >
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save</>}
            </Button>
            <Button
              data-testid="cancel-vehicle-type-btn"
              type="button"
              onClick={resetForm}
              className="font-ibm font-semibold h-10 px-6 rounded-lg text-white"
              style={{ backgroundColor: '#f59e0b' }}
            >
              <X className="w-4 h-4 mr-2" />Cancel
            </Button>
          </div>
        </div>
      </Card>

      {/* VEHICLE TYPES TABLE */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b-4 border-accent-blue px-6 py-4">
          <h2 className="text-base font-semibold text-text-dark font-manrope tracking-wide">VEHICLE TYPES</h2>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-7 h-7 animate-spin text-accent-blue" />
            <span className="ml-2 text-text-secondary font-ibm text-sm">Loading...</span>
          </div>
        ) : vehicleTypes.length === 0 ? (
          <div className="text-center py-12 text-text-secondary font-ibm text-sm">
            No vehicle types added yet. Use the form above to add one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="vehicle-types-table">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm w-12">#</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm w-56">Vehicle Type</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Vehicle Sizes</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm w-24">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {vehicleTypes.map((vt, index) => (
                  <tr key={vt.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm text-text-secondary font-ibm">{index + 1}</td>
                    <td className="px-6 py-4 text-sm font-medium text-text-dark font-ibm">{vt.type_name}</td>
                    <td className="px-6 py-4 text-sm text-text-secondary font-ibm">
                      {vt.sizes.map(s => s.size_name).join(', ') || '—'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`edit-vt-${vt.id}`}
                          onClick={() => handleEdit(vt)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4 text-accent-blue" />
                        </button>
                        <button
                          data-testid={`delete-vt-${vt.id}`}
                          onClick={() => { setRecordToDelete(vt); setDeleteDialogOpen(true); }}
                          className="w-8 h-8 flex items-center justify-center hover:bg-accent-red/10 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-accent-red" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vehicle Type</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{recordToDelete?.type_name}"? All associated vehicle sizes will also be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-accent-red hover:bg-accent-red/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VehicleTypeMaster;
